import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, CheckCircleIcon, TruckIcon, ShieldIcon, ZapIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface LocationPageProps {
  setPage: (page: string) => void;
}
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function LocationPageTemplate({
  setPage
}: LocationPageProps) {
  // Sample data for Detroit, MI
  const locationData = {
    city: 'Detroit',
    region: 'Metro Detroit',
    description: 'Providing professional, mobile dustless and dry blasting services directly to your site in Detroit and the surrounding metro area. From historic brick restoration to heavy industrial equipment cleaning, we bring the right equipment to get the job done fast and safely.'
  };
  const localServices = ['Historic Brick & Concrete Restoration', 'Industrial Machinery Blasting', 'Automotive & Classic Car Stripping', 'Graffiti Removal for Commercial Properties', 'Fleet Vehicle Surface Prep', 'Marine & Boat Bottom Cleaning'];
  return <div className="w-full bg-white" data-id="element-579" data-mp="2fqaw0ni9u1q5">
      {/* Hero Section */}
      <section className="bg-charcoal pt-20 pb-16 px-4 relative overflow-hidden" data-id="element-580" data-mp="33fddnvcwnukn">
        <div className="absolute inset-0 z-0 opacity-20" data-id="element-581" data-mp="38c3oo49urzz7">
          <img src="https://images.unsplash.com/photo-1580659369931-1e9672051052?q=80&w=1920&auto=format&fit=crop" alt="Detroit skyline" className="w-full h-full object-cover" data-id="element-582" data-mp="2ulqio7giuvci" />
          <div className="absolute inset-0 bg-charcoal/80 mix-blend-multiply" data-id="element-583" data-mp="3mceh3bqt9mmh"></div>
        </div>
        <div className="max-w-7xl mx-auto text-center relative z-10" data-id="element-584" data-mp="3r46bjxd38chw">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="inline-flex items-center space-x-2 bg-steel-900/80 border border-steel-700 px-4 py-2 rounded-full mb-6" data-id="element-585" data-mp="3r5hgi0i9rqal">
            <MapPinIcon className="w-4 h-4 text-brand-red" data-id="element-586" data-mp="3rxngbynype2d" />
            <span className="text-steel-200 text-sm font-heading font-bold uppercase tracking-wider" data-id="element-587" data-mp="3n041oxncqz9w">
              Serving {locationData.region}
            </span>
          </motion.div>
          <motion.h1 initial="hidden" animate="visible" variants={fadeInUp} className="text-4xl md:text-6xl font-heading font-extrabold text-white uppercase tracking-tight mb-6" data-id="element-588" data-mp="3da2ee82tpkw1">
            Mobile Dustless Blasting in{' '}
            <span className="text-brand-red" data-id="element-589" data-mp="3479ni5xjzb6q">{locationData.city}, MI</span>
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeInUp} className="text-lg md:text-xl text-steel-300 font-body max-w-3xl mx-auto leading-relaxed mb-8" data-id="element-590" data-mp="2zcdrkup743oc">
            {locationData.description}
          </motion.p>
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-591" data-mp="2p4hy72xoxll8">
            <Button variant="primary" size="lg" onClick={() => setPage('contact')} showPhoneIcon data-id="element-592" data-mp="2z2fu8clskmoe">
              Call (517) 777-8088
            </Button>
            <Button variant="outline" size="lg" onClick={() => setPage('contact')} data-id="element-593" data-mp="2bexwm0fuiudi">
              Get a Free Local Quote
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-white" data-id="element-594" data-mp="1iofxfai8td5d">
        <div className="max-w-7xl mx-auto px-4" data-id="element-595" data-mp="1nok8gl0dtfo7">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-596" data-mp="1nn0nwi7p0igm">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} data-id="element-597" data-mp="3pesr19gxa1zm">
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal mb-6" data-id="element-598" data-mp="30ri900i2ddq">
                Surface Prep Services in{' '}
                <span className="text-brand-red" data-id="element-599" data-mp="7yklplaf4tab">{locationData.city}</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-steel-600 mb-8 leading-relaxed" data-id="element-600" data-mp="lyc5y3rj35wr">
                As a fully mobile operation, Merem Blasting brings
                industrial-grade surface preparation directly to your facility,
                job site, or driveway in {locationData.region}. We handle
                everything from delicate historic restorations to aggressive
                industrial coating removal.
              </motion.p>
              <div className="space-y-4 mb-8" data-id="element-601" data-mp="137ef80qtdva8">
                {localServices.map((service, idx) => <motion.div key={idx} variants={fadeInUp} className="flex items-start" data-id="element-602" data-mp="oj0crqxb88u7" data-mpi={idx}>
                    <CheckCircleIcon className="w-6 h-6 text-brand-red mr-3 flex-shrink-0 mt-0.5" data-id="element-603" data-mp="fhk7typ9kyxd" />
                    <span className="text-charcoal font-semibold" data-id="element-604" data-mp="kgdjdlkzqfuw">
                      {service}
                    </span>
                  </motion.div>)}
              </div>
              <motion.div variants={fadeInUp} data-id="element-605" data-mp="aihbr3r9i31j">
                <Button variant="dark" onClick={() => setPage('services')} data-id="element-606" data-mp="aj8u3cvum49v">
                  View All Services
                </Button>
              </motion.div>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="relative h-96 rounded-sm overflow-hidden shadow-xl" data-id="element-607" data-mp="24vfhrb5xidj2">
              <img src="https://images.unsplash.com/photo-1513694203232-719a280e022f?q=80&w=1000&auto=format&fit=crop" alt={`Blasting services in ${locationData.city}`} className="w-full h-full object-cover" data-id="element-608" data-mp="1tgdj0fz68dqo" />
              <div className="absolute inset-0 bg-brand-red/10 mix-blend-multiply" data-id="element-609" data-mp="2h6pzif8hfisv"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us (Local Context) */}
      <section className="py-20 bg-steel-50" data-id="element-610" data-mp="28ee9guha5xdf">
        <div className="max-w-7xl mx-auto px-4 text-center" data-id="element-611" data-mp="28dmp6ft4vm2g">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal mb-12" data-id="element-612" data-mp="34w28pf3t7qv6">
            Why {locationData.city} Chooses Us
          </motion.h2>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-613" data-mp="ro53u9yzl02x">
            <motion.div variants={fadeInUp} className="bg-white p-8 border border-steel-200 shadow-sm" data-id="element-614" data-mp="15kqvh389dsow">
              <TruckIcon className="w-12 h-12 text-brand-red mx-auto mb-6" data-id="element-615" data-mp="1aik5668rt4tx" />
              <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-3" data-id="element-616" data-mp="1fcev3reyyg1g">
                Fully Mobile Setup
              </h3>
              <p className="text-steel-600 text-sm leading-relaxed" data-id="element-617" data-mp="1ocjogxnbvdwm">
                We bring our self-contained blasting rig directly to your{' '}
                {locationData.city} location. No need to transport heavy
                equipment or vehicles.
              </p>
            </motion.div>
            <motion.div variants={fadeInUp} className="bg-white p-8 border border-steel-200 shadow-sm" data-id="element-618" data-mp="z9nun4002j1s">
              <ShieldIcon className="w-12 h-12 text-brand-red mx-auto mb-6" data-id="element-619" data-mp="1d8dv8tm130uw" />
              <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-3" data-id="element-620" data-mp="198dc4vnzi1hi">
                Urban-Friendly Process
              </h3>
              <p className="text-steel-600 text-sm leading-relaxed" data-id="element-621" data-mp="lfdgthpjk9ai">
                Our low-mess dustless option helps hold down airborne dust,
                which suits populated metro areas and job sites with other
                trades working nearby.
              </p>
            </motion.div>
            <motion.div variants={fadeInUp} className="bg-white p-8 border border-steel-200 shadow-sm" data-id="element-622" data-mp="1tqtgzji84pj">
              <ZapIcon className="w-12 h-12 text-brand-red mx-auto mb-6" data-id="element-623" data-mp="3twi0pv9eoqi5" />
              <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-3" data-id="element-624" data-mp="3jt2wb7gutxwl">
                Fast Turnaround
              </h3>
              <p className="text-steel-600 text-sm leading-relaxed" data-id="element-625" data-mp="3olvz3psriw61">
                We know downtime costs money. We provide fast estimates and
                efficient on-site service to keep your project moving forward.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-brand-red text-white text-center px-4" data-id="element-626" data-mp="bdj5sg7kitl8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-3xl mx-auto" data-id="element-627" data-mp="bbq6xn375dns">
          <h2 className="text-4xl md:text-5xl font-heading font-extrabold uppercase mb-6" data-id="element-628" data-mp="2h6ra247wp4sf">
            Ready for Your Project in {locationData.city}?
          </h2>
          <p className="text-xl font-body mb-10 text-white/90" data-id="element-629" data-mp="2m75ihndf6gqz">
            Contact us today for a free, no-obligation estimate for your surface
            preparation needs.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-630" data-mp="2vfdc3zkzc1p3">
            <Button variant="secondary" size="lg" onClick={() => setPage('contact')} showPhoneIcon className="w-full sm:w-auto border-none" data-id="element-631" data-mp="2gq8t4g6d1tfx">
              Call (517) 777-8088
            </Button>
            <Button variant="outline-red" size="lg" onClick={() => setPage('contact')} className="w-full sm:w-auto" data-id="element-632" data-mp="2cpotn5ybedqm">
              Request a Quote
            </Button>
          </div>
        </motion.div>
      </section>
    </div>;
}